import { highlightMatch, titleCase } from "../../utils";

const MatchSealogEventsDisplay = ({
  data,
  searchTerm,
  eventValues,
  eventFreeTexts,
  totalMatches,
  timestamps,
  onTimestampClick,
  eventOptionNames,
  eventOptionValues,
}: {
  data: any;
  searchTerm: string;
  eventValues: string[];
  eventFreeTexts: string[];
  totalMatches: number;
  timestamps: string[];
  onTimestampClick: (timestamp: string) => void;
  eventOptionNames: string[];
  eventOptionValues: string[];
}) => {
  const displayLimit = 5;
  const displayedItems = data.items.slice(0, displayLimit);
  const extraCount = totalMatches - displayLimit;

  const highlightIfMatches = (field: string, searchTerm: string, selectedFilters: string[]) => {
    if (!field) return "";

    // Trim the search term to handle extra whitespace
    const trimmedSearchTerm = searchTerm?.trim().toLowerCase();
    const trimmedField = field.trim().toLowerCase();

    const isSearchTermMatch =
      trimmedSearchTerm && trimmedField.includes(trimmedSearchTerm);

    const isFieldValueMatch =
      selectedFilters && selectedFilters.includes(trimmedField);

    // If both match, highlight for search term and filters
    if (isSearchTermMatch && isFieldValueMatch) {
      return highlightMatch(field, trimmedSearchTerm || field);
    }
    // If only the search term matches
    else if (isSearchTermMatch) {
      return highlightMatch(field, trimmedSearchTerm);
    }
    // If only the filter matches
    else if (isFieldValueMatch) {
      return highlightMatch(field, field);
    }
    // If no match, just return title-cased field
    else {
      return titleCase(field);
    }
  };

  const getMatchedFields = (
    item: any,
    searchTerm: string,
    eventValues: string[],
    eventFreeTexts: string[],
    eventOptionNames: string[],
    eventOptionValues: string[]
  ) => {
    const { event } = item;
    const matches = [];

    matches.push(
      highlightIfMatches(titleCase(event.event_value), searchTerm, eventValues)
    );

    matches.push(
      highlightIfMatches(
        event.event_free_text.trim(),
        searchTerm,
        eventFreeTexts
      )
    );

    const highlightedOptions = event.event_options
      ?.map((option: any) => [
        highlightIfMatches(
          option.event_option_name,
          searchTerm,
          eventOptionNames
        ),
        highlightIfMatches(
          option.event_option_value,
          searchTerm,
          eventOptionValues
        ),
      ])
      .flat();

    matches.push(...highlightedOptions);

    return matches;
  };

  return (
    <div className="pt-1.5">
      <span className="text-md text-grey-200">
        {displayedItems.map((item: any, index: number) => {
          const matchedFields = getMatchedFields(
            item,
            searchTerm,
            eventValues,
            eventFreeTexts,
            eventOptionNames,
            eventOptionValues
          );
          const timestamp = timestamps[index];

          return (
            <span key={item.event.id} className="mb-2 inline">
              {timestamp && (
                <span
                  className="mr-1 text-blue-200 hover:cursor-pointer hover:underline"
                  onClick={() => onTimestampClick(timestamp)}
                >
                  {timestamp}:
                </span>
              )}
              {matchedFields.map((match, idx) => (
                <span key={`field-${item.event.id}-${idx}`} className="mr-2">
                  {match}
                </span>
              ))}
              {index < displayedItems.length - 1 ? ", " : ""}
            </span>
          );
        })}
        {extraCount > 0 && (
          <span>
            , and <strong>{extraCount}</strong> more matching sealog event(s)...
          </span>
        )}
      </span>
    </div>
  );
};

export default MatchSealogEventsDisplay;
